<template>
  <div class="animated fadeIn">
    <b-row class="mb-3 align-items-center">
      <b-col>
        <h4 class="mb-0">Log Sources</h4>
        <small class="text-muted">Which directories the log viewer may read. Files are read in place.</small>
      </b-col>
      <b-col cols="auto">
        <b-button size="sm" variant="outline-secondary" to="/application-logs/explorer" class="mr-2">
          <i class="fa fa-arrow-left mr-1"></i>Back to logs
        </b-button>
        <b-button v-if="$store.getters.canWrite" size="sm" variant="primary" @click="openCreate">
          <i class="fa fa-plus mr-1"></i>Add source
        </b-button>
      </b-col>
    </b-row>

    <b-alert :show="!databaseBacked" variant="warning" class="py-2">
      <strong>Read-only.</strong> The <code>tipsoi_admin</code> datasource is not configured, so these
      are the built-in defaults and cannot be edited. Reading logs still works.
    </b-alert>

    <b-card no-body class="shadow-sm">
      <b-table :items="sources" :fields="fields" :busy="loading" responsive striped hover show-empty
               empty-text="No log sources configured." class="mb-0" small>
        <template #table-busy>
          <div class="text-center py-4"><b-spinner variant="primary" /> Loading…</div>
        </template>
        <template #cell(displayName)="d">
          <strong>{{ d.value }}</strong>
          <div class="small text-muted">{{ d.item.code }}</div>
        </template>
        <template #cell(directory)="d">
          <code class="small">{{ d.value }}</code>
        </template>
        <template #cell(format)="d">
          <b-badge variant="light" class="border">{{ d.value }}</b-badge>
          <b-badge v-if="d.item.timezone !== 'Asia/Dhaka'" variant="info" class="ml-1">{{ d.item.timezone }}</b-badge>
        </template>
        <template #cell(allowRawDownload)="d">
          <b-badge :variant="d.value ? 'warning' : 'light'" class="border">{{ d.value ? 'raw allowed' : 'filtered only' }}</b-badge>
        </template>
        <template #cell(state)="d">
          <b-badge v-if="!d.item.enabled" variant="secondary">disabled</b-badge>
          <b-badge v-else-if="d.item.archived" variant="light" class="border">archived</b-badge>
          <b-badge v-else variant="success">active</b-badge>
        </template>
        <template #cell(actions)="d">
          <b-button v-if="$store.getters.canWrite && databaseBacked" size="sm" variant="outline-primary"
                    @click="openEdit(d.item)"><i class="fa fa-pencil"></i></b-button>
          <b-button v-if="$store.getters.canWrite && databaseBacked" size="sm" variant="outline-danger"
                    class="ml-1" @click="confirmDelete(d.item)"><i class="fa fa-trash"></i></b-button>
        </template>
      </b-table>
    </b-card>

    <p class="small text-muted mt-2 mb-0">
      Directories must resolve inside the roots allowed by <code>app.logs.allowed-roots</code> on the
      server. That boundary is deliberately not editable here. Raw whole-file download additionally
      requires the caller's username in <code>app.logs.raw-download-usernames</code>.
    </p>

    <!-- ── editor ───────────────────────────────────────────────────────── -->
    <b-modal id="log-source-editor" :title="form.id ? 'Edit log source' : 'Add log source'"
             size="lg" @ok="save" :ok-disabled="!$store.getters.canWrite || saving" ok-title="Save">
      <b-form>
        <b-form-row>
          <b-col md="6">
            <b-form-group label="Display name" description="Shown in the UI. Filenames are not trustworthy here.">
              <b-form-input v-model="form.displayName" size="sm" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Code" description="Appears in URLs. Lowercase, digits and hyphens.">
              <b-form-input v-model="form.code" size="sm" :disabled="!!form.id" />
            </b-form-group>
          </b-col>
        </b-form-row>

        <b-form-group label="Directory">
          <b-input-group size="sm">
            <b-form-input v-model="form.directory" placeholder="/home/inovace/tipsoi-hrm-application/…" />
            <b-input-group-append>
              <b-button variant="outline-secondary" @click="validate" :disabled="validating">
                <b-spinner v-if="validating" small /> Validate
              </b-button>
            </b-input-group-append>
          </b-input-group>
          <b-alert :show="!!validation" :variant="validation && validation.valid ? 'success' : 'danger'"
                   class="mt-2 py-2 small mb-0">
            <template v-if="validation && validation.valid">
              Resolves to <code>{{ validation.resolvedPath }}</code> ·
              <strong>{{ validation.matchedFiles }}</strong> matching file<span v-if="validation.matchedFiles !== 1">s</span>
              <span v-if="validation.detectedFormat"> · detected format <strong>{{ validation.detectedFormat }}</strong></span>
              <span v-if="validation.binary" class="text-danger"> · newest file is binary</span>
            </template>
            <template v-else-if="validation">{{ validation.message }}</template>
          </b-alert>
        </b-form-group>

        <b-form-row>
          <b-col md="6">
            <b-form-group label="Include globs" description="Comma-separated.">
              <b-form-input v-model="form.includeGlobs" size="sm" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Exclude globs" description="Wins over include. Keeps GC logs and binaries out.">
              <b-form-input v-model="form.excludeGlobs" size="sm" />
            </b-form-group>
          </b-col>
        </b-form-row>

        <b-form-row>
          <b-col md="4">
            <b-form-group label="Format" description="AUTO detects per file.">
              <b-form-select v-model="form.format" :options="formatOptions" size="sm" />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Timezone" description="location-tracking logs UTC; the rest are +06.">
              <b-form-input v-model="form.timezone" size="sm" />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Date filter uses" description="FILENAME when rollover crosses midnight in another zone.">
              <b-form-select v-model="form.dateFrom" :options="dateFromOptions" size="sm" />
            </b-form-group>
          </b-col>
        </b-form-row>

        <b-form-group label="Extra redaction patterns"
                      description="One regex per line, on top of the built-in secret masks. Validated on save.">
          <b-form-textarea v-model="form.redactionOverrides" rows="3" size="sm" />
        </b-form-group>

        <b-form-row>
          <b-col md="4">
            <b-form-checkbox v-model="form.allowRawDownload" switch>
              Allow raw download
              <div class="small text-muted">Still needs the username allowlist on the server.</div>
            </b-form-checkbox>
          </b-col>
          <b-col md="4"><b-form-checkbox v-model="form.archived" switch>Archived</b-form-checkbox></b-col>
          <b-col md="4"><b-form-checkbox v-model="form.enabled" switch>Enabled</b-form-checkbox></b-col>
        </b-form-row>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import logService from '@/services/logService'
import toastService from '@/services/toastService'

function blank() {
  return {
    id: null, code: '', displayName: '', directory: '',
    includeGlobs: '*.log,*.log.*,logfile',
    excludeGlobs: '*.gz,*.zip,*.index,*.timeindex,*-gc.log*,hs_err_pid*,*.png,*.svg,*.sql',
    format: 'AUTO', timezone: 'Asia/Dhaka', dateFrom: 'MTIME',
    redactionOverrides: '', allowRawDownload: false, archived: false, enabled: true, sortOrder: 50
  }
}

export default {
  name: 'LogSourceSettings',
  data: function() {
    return {
      sources: [],
      databaseBacked: true,
      loading: false,
      saving: false,
      validating: false,
      validation: null,
      form: blank(),
      formatOptions: ['AUTO', 'JAVA_LOGBACK', 'SPRING_ISO', 'PYTHON', 'KAFKA_LOG4J', 'WATCHDOG', 'LOGBACK_BOOT', 'RAW'],
      dateFromOptions: [
        { value: 'MTIME', text: 'File modified time' },
        { value: 'FILENAME', text: 'Date in the filename' }
      ],
      fields: [
        { key: 'displayName', label: 'Source' },
        { key: 'directory', label: 'Directory' },
        { key: 'format', label: 'Format' },
        { key: 'allowRawDownload', label: 'Download' },
        { key: 'state', label: 'State' },
        { key: 'actions', label: '', thStyle: 'width:90px' }
      ]
    }
  },
  created: function() { this.load() },
  methods: {
    load: function() {
      var self = this
      self.loading = true
      logService.getAllSources(function(data) {
        self.sources = data.sources || []
        self.databaseBacked = !!data.databaseBacked
        self.loading = false
      }, function(msg) { self.loading = false; toastService.showErrorToast(msg) })
    },
    openCreate: function() {
      this.form = blank()
      this.validation = null
      this.$bvModal.show('log-source-editor')
    },
    openEdit: function(row) {
      this.form = JSON.parse(JSON.stringify(row))
      this.validation = null
      this.$bvModal.show('log-source-editor')
    },
    validate: function() {
      var self = this
      self.validating = true
      self.validation = null
      logService.validateSource(
        { directory: self.form.directory, includeGlobs: self.form.includeGlobs,
          excludeGlobs: self.form.excludeGlobs, timezone: self.form.timezone },
        function(data) { self.validating = false; self.validation = data },
        function(msg) { self.validating = false; self.validation = { valid: false, message: msg } })
    },
    save: function(event) {
      var self = this
      event.preventDefault()
      self.saving = true
      var done = function() {
        self.saving = false
        self.$bvModal.hide('log-source-editor')
        toastService.showSuccessToast('Log source saved')
        self.load()
      }
      var fail = function(msg) { self.saving = false; toastService.showErrorToast(msg) }
      if (self.form.id) {
        logService.updateSource(self.form.id, self.form, done, fail)
      } else {
        logService.createSource(self.form, done, fail)
      }
    },
    confirmDelete: function(row) {
      var self = this
      self.$bvModal.msgBoxConfirm('Remove the log source "' + row.displayName + '"? The log files themselves are not touched.',
        { title: 'Remove source', okTitle: 'Remove', okVariant: 'danger' })
        .then(function(ok) {
          if (!ok) { return }
          logService.deleteSource(row.id,
            function() { toastService.showSuccessToast('Log source removed'); self.load() },
            function(msg) { toastService.showErrorToast(msg) })
        })
    }
  }
}
</script>
